import type { GeoHint, PeerId, SegmentId } from "@p2p-hls/protocol";

/**
 * Engine configuration. Timings are tuned in Phase 2 against real telemetry;
 * defaults below are conservative starting points for LIVE HLS.
 */
export interface EngineConfig {
  /** WebSocket URL of the tracker, e.g. "ws://localhost:8080". */
  readonly trackerUrl: string;
  /** Stream/swarm identifier. Every viewer of the same channel shares this. */
  readonly streamId: string;
  /** ICE servers (STUN + TURN). TURN is required for ~5-15% of peer pairs. */
  readonly iceServers: RTCIceServer[];
  /** Simulation-only geo hints appended to the tracker URL for local testing. */
  readonly geoSim?: Partial<Pick<GeoHint, "asn" | "country" | "region">>;

  /** Max concurrent peer connections in the mesh. */
  readonly maxPeers: number;
  /** Max concurrent segment requests in flight to a single peer. */
  readonly peerRequestConcurrency: number;
  /**
   * Max peers to split a single (playback-critical) segment across — BitTorrent-
   * style sub-segment swarming. Each peer serves a distinct chunk stride, so the
   * segment downloads using the swarm's aggregate upload. 1 disables swarming.
   */
  readonly maxSwarmPeers: number;

  /**
   * If a segment's play deadline is closer than this (ms), skip P2P and go
   * straight to HTTP — no time to gamble on a peer. Anti-rebuffer guard.
   */
  readonly httpOnlyDeadlineMs: number;
  /**
   * If a P2P request has not completed within this fraction of its remaining
   * deadline, fire a hedged HTTP request in parallel and take whichever wins.
   */
  readonly hedgeAtDeadlineFraction: number;
  /** Hard cap on how long to wait for a peer before abandoning to HTTP. */
  readonly peerRequestTimeoutMs: number;
  /**
   * If > 0, skip P2P and go straight to CDN when the best available peer's
   * MEASURED RTT exceeds this (ms). Unprobed peers (RTT not yet known) are still
   * allowed one attempt — the hedge race protects them. 0 disables the gate
   * (default; desktop/wifi). Raised on weak mobile links (see watchConnection).
   */
  readonly maxPeerRttMs: number;

  /** Number of segments retained/announced in the live sharing window. */
  readonly sharingWindowSize: number;
  /**
   * Byte budget for the segment store (held bytes cap). Bounds live heap
   * independent of bitrate — a count-only window churns 60–240 MB of multi-MB
   * segments at high bitrate → major-GC pauses / freezes. Default 48 MB.
   */
  readonly maxStoreBytes: number;

  /**
   * Prefetch: proactively pull up to this many upcoming segments (by media
   * sequence) from peers that already advertise them, ahead of the player
   * requesting them. This is what makes LIVE P2P work when viewers are only
   * slightly out of sync — the follower rides the leader instead of hitting the
   * CDN. 0 disables prefetch.
   */
  readonly prefetchAhead: number;
  /** Max concurrent prefetch requests in flight. */
  readonly maxConcurrentPrefetch: number;

  /**
   * Interval (ms) at which the client reports a compact metrics snapshot to the
   * tracker for the swarm-wide dashboard. 0 disables reporting.
   */
  readonly metricsReportIntervalMs: number;
  /**
   * Fraction (0..1) of peers that actually report metrics to the tracker. At
   * huge swarms, 3M peers × 1 report/5s = ~600k msg/s into the dashboard; sample
   * a subset (e.g. 0.05) since the aggregate is statistical. Default 1 (all).
   */
  readonly metricsSampleRate: number;

  /**
   * TEST-ONLY. Artificial delay (ms) a peer adds before serving a segment, used
   * by the benchmark harness to simulate a slow seeder so the deadline/hedge
   * logic is actually exercised. Leave 0 in production.
   */
  readonly debugServeDelayMs?: number;
}

export const DEFAULT_CONFIG: Omit<EngineConfig, "trackerUrl" | "streamId" | "iceServers"> = {
  maxPeers: 10,
  peerRequestConcurrency: 4,
  maxSwarmPeers: 4,
  httpOnlyDeadlineMs: 2500,
  hedgeAtDeadlineFraction: 0.5,
  peerRequestTimeoutMs: 4000,
  maxPeerRttMs: 0,
  sharingWindowSize: 30,
  maxStoreBytes: 48 * 1024 * 1024, // 48 MB — bounds heap regardless of bitrate
  prefetchAhead: 12,
  maxConcurrentPrefetch: 6,
  metricsReportIntervalMs: 5000,
  metricsSampleRate: 1,
  debugServeDelayMs: 0,
};

/** How a segment was ultimately delivered (for telemetry + UI). */
export type DeliverySource = "p2p" | "http" | "http-hedge-win" | "cache";

export interface SegmentResult {
  readonly seg: SegmentId;
  readonly data: Uint8Array;
  readonly source: DeliverySource;
  /** peerId if delivered via P2P, else null. */
  readonly fromPeer: PeerId | null;
  readonly elapsedMs: number;
}

/** Rolling counters surfaced to the demo UI / dashboard. */
export interface Telemetry {
  bytesP2P: number;
  bytesHttp: number;
  segmentsP2P: number;
  segmentsHttp: number;
  hedgeHttpWins: number;
  activePeers: number;
  /** P2P offload ratio in [0,1]. */
  offloadRatio(): number;
}

/** [absolute chunk index, chunk bytes]. */
export type ChunkEntry = [number, Uint8Array];
/** A (possibly strided) request result: the segment's total chunk count + the chunks this peer sent. */
export interface StridedResult {
  total: number;
  entries: ChunkEntry[];
}

/**
 * Transport-agnostic view of a connected peer. WebRTC is one implementation;
 * the native SDK provides another over libwebrtc. Scheduler/registry depend
 * only on this interface.
 */
export interface IPeer {
  readonly id: PeerId;
  readonly geo: GeoHint;
  /** Smoothed round-trip time in ms (EWMA), or Infinity until first probe. */
  rtt(): number;
  /** Smoothed download throughput from this peer, bytes/sec (0 until measured). */
  bandwidth(): number;
  /** True if reached via a TURN relay (high latency) — deprioritized in selection.
   *  Optional so lightweight/test peers need not implement it. */
  isRelayed?(): boolean;
  /** Does the peer currently advertise having this segment? */
  has(seg: SegmentId): boolean;
  /** Current number of in-flight requests to this peer. */
  inflight(): number;
  /** Request a segment; resolves with bytes or rejects (missing/busy/timeout). */
  request(seg: SegmentId, timeoutMs: number): Promise<Uint8Array>;
  /** Request only chunks with `index % stride === offset` (multi-peer swarming). */
  requestStrided(seg: SegmentId, stride: number, offset: number, timeoutMs: number): Promise<StridedResult>;
  /** Abort an in-flight request for this segment if any. */
  cancel(seg: SegmentId): void;
}
